import React, { useState, useEffect } from 'react';
import {
    Button,
    Typography,
    Box,
    Paper,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableRow,
    TableCell
} from '@mui/material';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrecotizacionPDF from './PrecotizacionPDF';
import {
    ValorKWService,
    RecursoSolarService,
    EmpresaService
} from '../../api/api';
import Fondo from '../../img/energia solar a tu altura.svg';
const ResumenPrecotizacion = ({ data, onReset, onBack }) => {
    const [ahorroMensual, setAhorroMensual] = useState(null);
    const [panelesEstimados, setPanelesEstimados] = useState(null);
    const [potenciaInstalada, setPotenciaInstalada] = useState(null);
    const [ahorroCO2, setAhorroCO2] = useState(null);
    const [totalProyecto, setTotalProyecto] = useState(null);
    const [detalleCalculo, setDetalleCalculo] = useState(null);
    const [empresaPrestadora, setEmpresaPrestadora] = useState(null);
    const [loadingCalculos, setLoadingCalculos] = useState(true);
    const [errorCalculos, setErrorCalculos] = useState(null);
    const [apiCallsCompleted, setApiCallsCompleted] = useState(0);

    // Constantes para los cálculos
    const paneles = 550;
    const coeficiente = 1.2;
    const factorCO2 = 0.388;
    const totalApiCalls = 3; // Empresa, ValorKW y RecursoSolar

    const mapContainerStyle = {
        width: '100%',
        height: '300px',
        marginTop: '20px'
    };

    useEffect(() => {
        const calcularResultados = async () => {
            try {
                setLoadingCalculos(true);
                setErrorCalculos(null);
                setApiCallsCompleted(0);

                // 1. Calcular ahorro mensual (no requiere API)
                const valorFacturaNumerico = parseFloat(data.valorFactura?.replace(/[^0-9]/g, '') || '0');
                setAhorroMensual(valorFacturaNumerico * 0.95);

                // 2. Obtener empresa prestadora del servicio
                let empresaPrestadoraData = null;
                if (data.departamento?.Id) {
                    const empresasResponse = await EmpresaService.obtenerPorDepartamento(data.departamento.Id);
                    setApiCallsCompleted(prev => prev + 1);

                    if (empresasResponse.data?.length > 0) {
                        empresaPrestadoraData = empresasResponse.data[0];
                        setEmpresaPrestadora(empresaPrestadoraData);
                    } else {
                        throw new Error('No se encontró empresa prestadora para este departamento');
                    }
                }

                // 3. Calcular número de paneles estimados
                if (data.municipio?.Id && empresaPrestadoraData?.Id && data.estrato?.Id) {
                    // Obtener valor KW prestadora y recurso solar en paralelo
                    const [valorKWResponse, recursoSolarResponse] = await Promise.all([
                        ValorKWService.obtenerPorEmpresaEstrato(empresaPrestadoraData.Id, data.estrato.Id),
                        RecursoSolarService.obtenerPorCiudad(data.municipio.Id)
                    ]);

                    setApiCallsCompleted(prev => prev + 2);

                    const valorKW = valorKWResponse.data[0]?.Valor;
                    const recursoSolar = recursoSolarResponse.data[0]?.Valor;

                    if (!valorKW || !recursoSolar) {
                        throw new Error(!valorKW ? 'No se encontró valor KW' : 'No se encontró recurso solar');
                    }

                    // Realizar todos los cálculos
                    const consumoDiario = valorFacturaNumerico / valorKW / 30;
                    const energiaNecesaria = consumoDiario / recursoSolar * 1000;
                    const panelesCalculados = Math.ceil(energiaNecesaria / paneles * coeficiente);

                    const potencia = (panelesCalculados * 500) / 1000;
                    const co2 = (potencia * recursoSolar * 30 * 0.8 * 12) / 1000 / factorCO2;
                    const totalProyectoCalc = panelesCalculados * (data.municipio.Kwp || 5000000);

                    setPanelesEstimados(panelesCalculados);
                    setPotenciaInstalada(potencia);
                    setAhorroCO2(co2);
                    setTotalProyecto(totalProyectoCalc);

                    setDetalleCalculo({
                        valorFactura: valorFacturaNumerico,
                        valorKW,
                        recursoSolar,
                        panelesCalculados,
                        potenciaInstalada: potencia,
                        ahorroCO2: co2,
                        totalProyecto: totalProyectoCalc,
                        empresaPrestadora: empresaPrestadoraData.Nombre,
                        pasosPaneles: {
                            paso1: valorFacturaNumerico / valorKW,
                            paso2: (valorFacturaNumerico / valorKW) / 30,
                            paso3: ((valorFacturaNumerico / valorKW) / 30) / recursoSolar,
                            paso4: (((valorFacturaNumerico / valorKW) / 30) / recursoSolar) * 1000,
                            paso5: ((((valorFacturaNumerico / valorKW) / 30) / recursoSolar) * 1000) / paneles,
                            paso6: (((((valorFacturaNumerico / valorKW) / 30) / recursoSolar) * 1000) / paneles * coeficiente),
                            panelesConstante: paneles,
                            coeficienteConstante: coeficiente
                        },
                        pasosPotencia: {
                            paso1: panelesCalculados * 500,
                            paso2: (panelesCalculados * 500) / 1000
                        },
                        pasosCO2: {
                            paso1: potencia * recursoSolar,
                            paso2: potencia * recursoSolar * 30,
                            paso3: potencia * recursoSolar * 30 * 0.8,
                            paso4: potencia * recursoSolar * 30 * 0.8 * 12,
                            paso5: (potencia * recursoSolar * 30 * 0.8 * 12) / 1000,
                            paso6: (potencia * recursoSolar * 30 * 0.8 * 12) / 1000 / factorCO2,
                            factorCO2: factorCO2
                        },
                        pasosTotalProyecto: {
                            paso1: panelesCalculados,
                            paso2: data.municipio.Kwp || 5000000,
                            paso3: panelesCalculados * (data.municipio.Kwp || 5000000)
                        }
                    });
                }
            } catch (error) {
                console.error('Error en cálculos:', error);
                setErrorCalculos(error.message || 'Error al realizar los cálculos');
            } finally {
                setLoadingCalculos(false);
            }
        };

        if (data.valorFactura && data.departamento) {
            calcularResultados();
        }
    }, [data]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatNumber = (value, decimals = 2) => {
        return new Intl.NumberFormat('es-CO', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(value);
    };

    const formatCO2 = (value) => {
        return `${formatNumber(value, 3)} toneladas`;
    };

    return (
        <div>
            <Typography variant="h5" gutterBottom>
                Resumen de tu precotización
            </Typography>

            {loadingCalculos && (
                <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={(apiCallsCompleted / totalApiCalls) * 100} />
                    <Typography variant="caption" display="block" textAlign="center">
                        Cargando datos: {apiCallsCompleted} de {totalApiCalls} completados
                    </Typography>
                </Box>
            )}

            {errorCalculos && (
                <Typography color="error" sx={{ mb: 2 }}>
                    Error en cálculos: {errorCalculos}
                </Typography>
            )}

            <PrecotizacionPDF
                data={{
                    ...data,
                    ahorroMensual,
                    panelesEstimados,
                    potenciaInstalada,
                    ahorroCO2,
                    totalProyecto,
                }}
                Fondo

                onBack={onBack}
            />
        </div>
    );
};

export default ResumenPrecotizacion;